import { Link } from "wouter";
import { ArrowRight, Check, MapPin, Clock, Truck, Shield, Zap, Target } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead, { createLocalBusinessSchema } from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import PageHero from "@/components/PageHero";
import { IMAGES, CONTACT } from "@/lib/constants";
import LazyImage from "@/components/LazyImage";

const localBenefits = [
  {
    icon: Truck,
    title: "Effektiv leverans till Linköping",
    text: "Vi planerar pålitliga leveranser från Eskilstuna till Linköping utifrån projektets omfattning och önskat datum.",
  },
  {
    icon: Clock,
    title: "Korta ledtider för high-tech",
    text: "Med tre CO2-lasrar och hög kapacitet kan vi möta snäva tidsramar inom Linköpings högteknologiska industrier.",
  },
  {
    icon: Shield,
    title: "Garanterad precision för flyg & försvar",
    text: "Toleranser ner till 0,1 mm med moderna CO2-lasrar. Vi har erfarenhet av de höga krav som ställs av flyg- och försvarsindustrin.",
  },
  {
    icon: Target,
    title: "Helhetslösning för tillverkning",
    text: "Kombinera laserskärning med kantpressning och svetsning. Vi levererar kompletta, monteringsfärdiga komponenter direkt till er i Linköping.",
  },
];

const linkopingIndustries = [
  {
    name: "Flyg- & Försvarsindustri",
    desc: "Vi har kapacitet för uppdrag inom flyg- och försvarssektorn, där kraven på precision, material och dokumentation är höga.",
  },
  {
    name: "Högteknologisk Tillverkning",
    desc: "Linköpings tekniska framkant kräver avancerad tillverkning. Vi levererar komponenter för allt från mätteknik till automation.",
  },
  {
    name: "Universitet & Forskning",
    desc: "Vi samarbetar med tekniska institutioner och forskningsprojekt vid Linköpings universitet för att ta fram prototyper och specialkomponenter.",
  },
  {
    name: "Fordon & Transport",
    desc: "Precisionskomponenter för fordonsindustrin och transportsektorn, där innovation och tillförlitlighet är nyckeln.",
  },
];

const capabilities = [
  { material: "Svart stål", maxThickness: "25 mm", minThickness: "0,5 mm" },
  { material: "Rostfritt stål", maxThickness: "20 mm", minThickness: "0,5 mm" },
  { material: "Aluminium", maxThickness: "15 mm", minThickness: "0,5 mm" },
  { material: "Höghållfasthetsstål (Strenx)", maxThickness: "20 mm", minThickness: "1 mm" },
  { material: "Slitstål (Hardox/Raex)", maxThickness: "20 mm", minThickness: "1 mm" },
];

export default function LaserskarningLinkoping() {
  const localServiceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Laserskärning Linköping",
    description:
      "Högpresterande laserskärning för Linköpings teknik- och flygindustri. ASPAB levererar med toleranser ner till 0,1 mm, med expertis inom avancerade material.",
    provider: {
      "@type": "LocalBusiness",
      name: "ASPAB",
      telephone: "+46161326 00",
      email: "order@aspab.com",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Abramsons väg 2",
        addressLocality: "Eskilstuna",
        postalCode: "632 39",
        addressCountry: "SE",
      },
    },
    areaServed: {
      "@type": "City",
      name: "Linköping",
      containedInPlace: {
        "@type": "Country",
        name: "Sweden",
      },
    },
    serviceType: "Laserskärning",
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Laserskärningstjänster",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Laserskärning stål",
            description: "Laserskärning i svart stål upp till 25 mm tjocklek",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Laserskärning rostfritt",
            description: "Laserskärning i rostfritt stål upp till 20 mm tjocklek",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Laserskärning aluminium",
            description: "Laserskärning i aluminium upp till 15 mm tjocklek",
          },
        },
      ],
    },
  };

  const localBusinessSchema = createLocalBusinessSchema({
    areaServed: "Linköping",
    serviceDescription: "Specialiserad laserskärning, kantpressning och svetsning för företag i Linköping med omnejd. Partner till flyg-, försvars- och teknikindustrin.",
  });

  return (
    <Layout>
      <SEOHead
        title="Laserskärning Linköping – För Flyg & High-Tech | ASPAB"
        description="ASPAB är er partner för laserskärning i Linköping. Vi är specialiserade på komponenter till flyg, försvar och högteknologisk industri. Begär offert idag!"
        canonical="https://aspab.com/laserskarning-linkoping"
        path="/laserskarning-linkoping"
        keywords="laserskärning Linköping, plåtbearbetning Linköping, flygindustri, försvarsindustri, laserskärning Östergötland"
        breadcrumbs={[
          { name: "Laserskärning", href: "/tjanster/laserskarning" },
          { name: "Laserskärning Linköping", href: "/laserskarning-linkoping" },
        ]}
        structuredData={[localServiceSchema, localBusinessSchema]}
        ogImage={IMAGES.ogLinkoping}
      />

      <PageHero
        title="Laserskärning Linköping"
        subtitle="Precision och expertis för Linköpings högteknologiska industri. Vi är er strategiska partner för avancerad laserskärning, från prototyp till serieproduktion."
        breadcrumbs={[
          { name: "Laserskärning", href: "/tjanster/laserskarning" },
          { name: "Laserskärning Linköping", href: "/laserskarning-linkoping" },
        ]}
        image={IMAGES.heroLaser}
      />

      {/* Intro – Varför välja ASPAB från Linköping */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14">
            <AnimatedSection>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                  Partner till Linköpings industri
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Specialister på laserskärning för flyg, försvar och tech
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  Från vår verkstad i Eskilstuna hjälper vi företag i Linköpings industrilandskap, inklusive flyg-, försvars- och tekniksektorn. Planerade transporter gör oss till en tillgänglig partner för både prototyper och återkommande produktion.
                </p>
                <p className="text-gray-500 mt-4 leading-relaxed">
                  Vi förstår de unika kraven som ställs av högteknologiska företag och forskningsintensiva miljöer som Linköpings universitet. Vår expertis inom avancerade material och snäva toleranser gör oss till ett självklart val för företag som inte kompromissar med kvalitet.
                </p>
                <div className="flex items-center gap-3 mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <MapPin className="w-5 h-5 text-[#2563EB] shrink-0" />
                  <div>
                    <span className="text-gray-900 font-medium text-sm">Leverans från ASPAB i Eskilstuna till Linköping</span>
                    <span className="text-gray-500 text-sm block">Effektiv logistik längs E4/väg 55</span>
                  </div>
                </div>
                <Link
                  href="/kontakt"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#2563EB] text-white font-semibold rounded-md hover:bg-[#1D4ED8] transition-colors mt-6"
                >
                  Begär offert för ert projekt <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <div className="space-y-4">
                <LazyImage
                  src={IMAGES.laserOldSite}
                  alt="Laserskärning för företag i Linköping – ASPAB CO2 Laser"
                  className="w-full rounded-lg"
                />
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">0,1 mm</div>
                    <div className="text-gray-500 text-sm mt-1">Tolerans</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">4</div>
                    <div className="text-gray-500 text-sm mt-1">Lasermaskiner</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">25 mm</div>
                    <div className="text-gray-500 text-sm mt-1">Max tjocklek stål</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">30+</div>
                    <div className="text-gray-500 text-sm mt-1">Års erfarenhet</div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Fördelar med ASPAB för Linköpingsföretag */}
      <section className="bg-gray-50 py-20 md:py-28 border-t border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-2xl mb-14">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                Fördelar
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                Varför Linköpingsföretag väljer ASPAB
              </h2>
              <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                Vi kombinerar geografisk närhet med spetskompetens och en modern maskinpark, vilket gör oss till en idealisk partner för industrin i Linköping.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-6">
            {localBenefits.map((benefit, i) => (
              <AnimatedSection key={benefit.title} delay={i * 120}>
                <div className="bg-white border border-gray-200 rounded-lg p-6 h-full hover:border-[#2563EB]/20 hover:shadow-md transition-all duration-300">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 shrink-0 flex items-center justify-center bg-[#2563EB]/10 rounded-lg">
                      <benefit.icon className="w-6 h-6 text-[#2563EB]" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-gray-900">{benefit.title}</h3>
                      <p className="text-gray-500 text-sm mt-2 leading-relaxed">{benefit.text}</p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Branscher i Linköping */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <AnimatedSection>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                  Branscher vi betjänar
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Laserskärning för Linköpings industrier
                </h2>
                <p className="text-gray-500 mt-4 leading-relaxed">
                  Linköping är en motor inom svensk högteknologi. Vi är stolta över att leverera kritiska komponenter till flera av regionens nyckelindustrier.
                </p>
                <div className="mt-8 space-y-5">
                  {linkopingIndustries.map((ind) => (
                    <div key={ind.name} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#2563EB] shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-gray-900 font-semibold">{ind.name}</h3>
                        <p className="text-gray-500 text-sm mt-0.5">{ind.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <div className="overflow-hidden rounded-lg">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full aspect-video object-cover rounded-lg"
                  poster={IMAGES.heroLaser}
                >
                  <source src={IMAGES.laserVideo} type="video/mp4" />
                </video>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Tekniska specifikationer */}
      <section className="bg-gray-50 py-20 md:py-28 border-t border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-2xl mb-12">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                Kapacitet
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                Material och tjocklekar vi hanterar
              </h2>
              <p className="text-gray-500 mt-4 leading-relaxed">
                Med fyra moderna lasermaskiner från Trumpf och Amada har vi kapacitet att skära i ett brett utbud av material med garanterade toleranser på 0,1 mm.
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={100}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-4 px-6 text-gray-500 text-sm font-medium">Material</th>
                    <th className="text-left py-4 px-6 text-gray-500 text-sm font-medium">Min tjocklek</th>
                    <th className="text-left py-4 px-6 text-gray-500 text-sm font-medium">Max tjocklek</th>
                    <th className="text-left py-4 px-6 text-gray-500 text-sm font-medium">Tolerans</th>
                  </tr>
                </thead>
                <tbody>
                  {capabilities.map((cap) => (
                    <tr key={cap.material} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="py-4 px-6 text-gray-900 font-medium">{cap.material}</td>
                      <td className="py-4 px-6 text-gray-500">{cap.minThickness}</td>
                      <td className="py-4 px-6 text-gray-500">{cap.maxThickness}</td>
                      <td className="py-4 px-6 text-[#2563EB] font-medium">0,1 mm</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Process */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-2xl mb-12">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                Så fungerar det
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                Beställ laserskärning från Linköping – enkelt och smidigt
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                step: "01",
                title: "Skicka förfrågan",
                desc: "Ladda upp er CAD-fil, PDF eller skiss via vårt kontaktformulär. Vi hanterar alla vanliga filformat.",
              },
              {
                step: "02",
                title: "Snabb offert",
                desc: "Vi granskar underlaget och återkommer med en detaljerad offert, materialrekommendation och leveranstid.",
              },
              {
                step: "03",
                title: "Produktion",
                desc: "Skärning med CO2 laser, med första bit-kontroll och löpande kvalitetskontroll under hela produktionen.",
              },
              {
                step: "04",
                title: "Leverans till Linköping",
                desc: "Leverans med egen lastbil eller speditör direkt till er adress i Linköping med omnejd.",
              },
            ].map((item, i) => (
              <AnimatedSection key={item.step} delay={i * 120}>
                <div className="bg-white border border-gray-200 rounded-lg p-6 h-full hover:border-[#2563EB]/20 transition-colors">
                  <div className="font-heading text-3xl font-bold text-[#2563EB]/20">{item.step}</div>
                  <h3 className="font-heading text-lg font-semibold text-gray-900 mt-2">{item.title}</h3>
                  <p className="text-gray-500 text-sm mt-2 leading-relaxed">{item.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Relaterade tjänster */}
      <section className="bg-gray-50 py-16 md:py-20 border-t border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-2xl mb-10">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                Komplett metallbearbetning
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mt-3">
                Mer än bara laserskärning
              </h2>
              <p className="text-gray-500 mt-3 leading-relaxed">
                Utöver laserskärning erbjuder vi kantpressning och svetsning – allt under ett tak i Eskilstuna. Få färdiga komponenter levererade direkt till Linköping.
              </p>
            </div>
          </AnimatedSection>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: "Laserskärning",
                desc: "Tre CO2-lasrar för stål, rostfritt och aluminium med toleranser ner till 0,1 mm.",
                href: "/tjanster/laserskarning",
              },
              {
                title: "Kantpressning",
                desc: "Tre kantpressar upp till 310 ton för exakt bockning av plåt.",
                href: "/tjanster/kantpressning",
              },
              {
                title: "Svetsning",
                desc: "Svetsning av stål, rostfritt och aluminium för hållbara konstruktioner.",
                href: "/tjanster/svetsning",
              },
            ].map((svc, i) => (
              <AnimatedSection key={svc.title} delay={i * 100}>
                <Link href={svc.href} className="group block">
                  <div className="bg-white border border-gray-200 rounded-lg p-6 h-full hover:border-[#2563EB]/30 hover:shadow-md transition-all duration-300">
                    <h3 className="font-heading text-lg font-semibold text-gray-900 group-hover:text-[#2563EB] transition-colors">
                      {svc.title}
                    </h3>
                    <p className="text-gray-500 text-sm mt-2 leading-relaxed">{svc.desc}</p>
                    <span className="inline-flex items-center gap-1 text-[#2563EB] text-sm font-medium mt-4 group-hover:gap-2 transition-all">
                      Läs mer <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </Link>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#2563EB] py-16 md:py-20">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white">
                Behöver ni laserskärning i Linköping?
              </h2>
              <p className="text-white mt-4 text-lg leading-relaxed">
                Kontakta oss idag för en kostnadsfri offert. Vi är en pålitlig partner till Linköpings industri och levererar med snabbhet och precision.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
                <Link
                  href="/kontakt"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#2563EB] font-semibold rounded-md hover:bg-gray-100 transition-colors"
                >
                  Begär offert <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href={`tel:${CONTACT.phone.replace(/[^0-9+]/g, "")}`}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 border border-white/20 text-white font-medium rounded-md hover:bg-white/20 transition-colors"
                >
                  Ring {CONTACT.phone}
                </a>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
